/**
 * Gender Management System
 * Manages site gender separation through localStorage
 */

class GenderManager {
  constructor() {
    this.storageKey = "site_gender";
    this.defaultGender = "woman";

    this.init();
  }

  /**
   * Initialize gender management system
   */
  init() {
    // Set window.gender immediately from localStorage or detect from page
    this.setWindowGender();
    
    // Detect and set gender on page load
    this.detectAndSetGender();

    // Apply active states to menu
    this.applyActiveStates();

    // Listen for localStorage changes (for tab synchronization)
    window.addEventListener("storage", (e) => {
      if (e.key === this.storageKey) {
        this.setWindowGender();
        this.applyActiveStates();
      }
    });
  }

  /**
   * Detect and set gender based on current page
   */
  detectAndSetGender() {
    const detectedGender = this.detectGenderFromPage();
    if (detectedGender) {
      this.setGender(detectedGender);
    }
  }

  /**
   * Detect gender based on current page
   */
  detectGenderFromPage() {
    const pathname = window.location.pathname;
    const currentUrl = window.location.href;

    if (window.productGender) {
      return window.productGender;
    }
    if (pathname === "/") {
      return "woman";
    }

    if (pathname === "/pages/homepage-for-men") {
      return "men";
    }

    if (currentUrl.indexOf("woman") !== -1) {
      return "woman";
    }

    if (currentUrl.indexOf("men") !== -1 || currentUrl.indexOf("man") !== -1) {
      return "men";
    }
    if (localStorage.getItem(this.storageKey)) {
      return localStorage.getItem(this.storageKey) == "men" ? "men" : "woman";
    }

    return null;
  }

  /**
   * Get current gender from localStorage
   */
  getCurrentGender() {
    try {
      return localStorage.getItem(this.storageKey);
    } catch (e) {
      console.warn("Failed to get gender from localStorage:", e);
      return null;
    }
  }

  /**
   * Set gender in localStorage and window
   */
  setGender(gender) {
    try {
      localStorage.setItem(this.storageKey, gender);
      this.setWindowGender();
      this.applyActiveStates();

      window.dispatchEvent(
        new CustomEvent("genderChanged", {
          detail: { gender: gender },
        })
      );

      console.log(`Gender set: ${gender}`);
    } catch (e) {
      console.warn("Failed to save gender to localStorage:", e);
    }
  }

  /**
   * Set window.gender for global access
   */
  setWindowGender() {
    // First try to get from localStorage
    let currentGender = this.getCurrentGender();
    
    // If not in localStorage, detect from current page
    if (!currentGender) {
      currentGender = this.detectGenderFromPage();
    }
    
    // Fallback to default
    if (!currentGender) {
      currentGender = this.defaultGender;
    }
    
    window.gender = currentGender;
  }

  /**
   * Apply active states based on current gender
   */
  applyActiveStates() {
    let womanLinks = document.querySelectorAll('[data-gender="woman"]');
    let manLinks = document.querySelectorAll('[data-gender="men"]');
    womanLinks.forEach((link) => {
      if (window.gender === "woman") {
        link.classList.add("active");
        link.querySelector("a").classList.add("header__active-menu-item");
        link.querySelector(".header__submenu").style.display = "block";
      } else {
        link.classList.remove("active");
        link.querySelector("a").classList.remove("header__active-menu-item");
        link.querySelector(".header__submenu").style.display = "none";
      }
    });
    manLinks.forEach((link) => {
      if (window.gender === "men") {
        link.classList.add("active");
        link.querySelector("a").classList.add("header__active-menu-item");
        link.querySelector(".header__submenu").style.display = "block";
      } else {
        link.classList.remove("active");
        link.querySelector("a").classList.remove("header__active-menu-item");
        link.querySelector(".header__submenu").style.display = "none";
      }
    });

    let rootLinks = document.querySelectorAll("[href='/']:not(.list-menu__item)");
    rootLinks.forEach((link) => {
      if (window.gender === "men") {
        link.href = "/pages/homepage-for-men";
      }
    });
  }

  /**
   * Get current gender (public method)
   */
  getGender() {
    return this.getCurrentGender() || this.defaultGender;
  }

  /**
   * Force set gender (public method)
   */
  setGenderForced(gender) {
    this.setGender(gender);
  }

  /**
   * Update active states (public method)
   */
  updateActiveStates() {
    this.applyActiveStates();
  }
  /**
   * Transform URL from one gender to another
   * @param {string} url - Current URL
   * @param {string} fromGender - Current gender ('woman' or 'men')
   * @param {string} toGender - Target gender ('woman' or 'men')
   * @returns {string} - Transformed URL
   */
  transformUrlForGender(url, fromGender, toGender) {
    if (fromGender === toGender) return url;
    
    // Handle homepage cases
    if (url === '/' && toGender === 'men') {
      return '/pages/homepage-for-men';
    }
    if (url === '/pages/homepage-for-men' && toGender === 'woman') {
      return '/';
    }
    
    // Check if URL contains gender-specific parts
    const hasGenderInUrl = url.includes('woman') || url.includes('man') || url.includes('man');
    
    // If URL doesn't contain gender info, redirect to default page for target gender
    if (!hasGenderInUrl) {
      return toGender === 'men' ? '/pages/homepage-for-men' : '/';
    }
    
    // Replace gender-specific parts in URL
    let transformedUrl = url;
    
    if (fromGender === 'woman' && toGender === 'men') {
      // Replace 'woman' with 'men' in URL
      transformedUrl = transformedUrl.replace(/\/woman\//g, '/man/');
      transformedUrl = transformedUrl.replace(/\/woman-/g, '/man-');
      transformedUrl = transformedUrl.replace(/-woman\//g, '-man/');
      transformedUrl = transformedUrl.replace(/-woman-/g, '-man-');
      transformedUrl = transformedUrl.replace(/-woman$/g, '-man');
      transformedUrl = transformedUrl.replace(/\/woman$/g, '/man');
    } else if (fromGender === 'men' && toGender === 'woman') {
      // Replace 'men' with 'woman' in URL
      transformedUrl = transformedUrl.replace(/\/man\//g, '/woman/');
      transformedUrl = transformedUrl.replace(/\/man-/g, '/woman-');
      transformedUrl = transformedUrl.replace(/-man\//g, '-woman/');
      transformedUrl = transformedUrl.replace(/-man-/g, '-woman-');
      transformedUrl = transformedUrl.replace(/-man$/g, '-woman');
      transformedUrl = transformedUrl.replace(/\/man$/g, '/woman');
    }
    
    return transformedUrl;
  }

  /**
   * Check if a URL exists by making a HEAD request
   * @param {string} url - URL to check
   * @returns {Promise<boolean>} - True if URL exists
   */
  async checkUrlExists(url) {
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        cache: 'no-cache'
      });
      return response.ok;
    } catch (error) {
      console.warn('Error checking URL existence:', error);
      return false;
    }
  }

  /**
   * Handle gender switching navigation
   * @param {string} targetGender - Target gender to switch to
   * @param {Event} event - Click event
   */
  async handleGenderSwitch(targetGender, event) {
    event.preventDefault();
    
    const currentUrl = window.location.pathname;
    const currentGender = this.getCurrentGender() || this.defaultGender;
    
    // Don't switch if already on target gender
    if (currentGender === targetGender) {
      window.location.href = event.target.href;
      return;
    }
    
    // Transform current URL to target gender
    const transformedUrl = this.transformUrlForGender(currentUrl, currentGender, targetGender);
    
    // Check if transformed URL exists
    const urlExists = await this.checkUrlExists(transformedUrl);
    
    let finalUrl;
    if (urlExists) {
      // Use transformed URL if it exists
      finalUrl = transformedUrl;
    } else {
      // Fallback to default homepage for target gender
      finalUrl = targetGender === 'men' ? '/pages/homepage-for-men' : '/';
    }
    
    // Set gender and navigate
    this.setGender(targetGender);
    window.location.href = finalUrl;
  }
}


// Set initial gender before DOM is ready
(function() {
  // Quick gender detection for immediate access
  function getInitialGender() {
    const pathname = window.location.pathname;
    const currentUrl = window.location.href;
    
    // Check localStorage first
    try {
      const stored = localStorage.getItem('site_gender');
      if (stored) return stored;
    } catch (e) {}
    
    // Detect from page
    if (pathname === "/") return "woman";
    if (pathname === "/pages/homepage-for-men") return "men";
    if (currentUrl.indexOf("woman") !== -1) return "woman";
    if (currentUrl.indexOf("men") !== -1 || currentUrl.indexOf("man") !== -1) return "men";
    
    return "woman"; // default
  }
  
  // Set window.gender immediately
  window.gender = getInitialGender();
})();

// Initialize on DOM load
document.addEventListener("DOMContentLoaded", () => {
  window.genderManager = new GenderManager();
  
  // Add gender switching event listeners
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    
    const genderElement = link.closest('[data-gender]');
    if (!genderElement) return;
    
    const targetGender = genderElement.getAttribute('data-gender');
    const currentGender = window.genderManager?.getCurrentGender() || 'woman';
    
    // Only handle gender switching clicks (when clicking on different gender)
    if (targetGender && targetGender !== currentGender) {
      e.preventDefault();
      window.genderManager.handleGenderSwitch(targetGender, e);
    }
  });
  
});

// Listen for page changes (for dynamic content)
window.addEventListener("popstate", () => {
  if (window.genderManager) {
    window.genderManager.detectAndSetGender();
    window.genderManager.updateActiveStates();
  }
});

// Listen for navigation events (if using AJAX navigation)
document.addEventListener("click", (e) => {
  const link = e.target.closest("a[href]");
  if (link && !link.target && !e.ctrlKey && !e.metaKey) {
    setTimeout(() => {
      if (window.genderManager) {
        window.genderManager.detectAndSetGender();
        window.genderManager.updateActiveStates();
      }
    }, 100);
  }
});

if (typeof module !== "undefined" && module.exports) {
  module.exports = GenderManager;
}


function hideSingleValueFilters() {
    const filters = document.querySelectorAll('.cloud-search-filter');
    
    filters.forEach(filter => {
        const valuesContainer = filter.querySelector('.cloud-search-filter__values');
        
        if (valuesContainer) {
            const valueElements = valuesContainer.querySelectorAll('.cloud-search-filter-value');
            
            if (valueElements.length === 1) {
                filter.style.display = 'none';
                // filter.style.opacity = '0';
                // filter.style.visibility = 'hidden';
                // filter.style.position = 'absolute';
                // filter.style.left = '-9999px';
            }
        }
    });
}

// Global filter management for AppCode compatibility
function initializeGlobalFilters() {
    const checkGlobalManager = () => {
        if (window.globalSizeFilterManager) {
            window.globalSizeFilterManager.waitForFiltersAndApply();
        } else {
            setTimeout(checkGlobalManager, 100);
        }
    };
    
    checkGlobalManager();
    
    // Additional checks for AppCode pages
    setTimeout(() => {
        if (window.globalSizeFilterManager) {
            window.globalSizeFilterManager.waitForFiltersAndApply();
        }
    }, 2000);
    
    setTimeout(() => {
        if (window.globalSizeFilterManager) {
            window.globalSizeFilterManager.waitForFiltersAndApply();
        }
    }, 5000);
}

document.addEventListener('DOMContentLoaded', () => {
    hideSingleValueFilters();
    initializeGlobalFilters();
});

const observer = new MutationObserver((mutations) => {
    const hasNewFilters = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
            return node.nodeType === 1 && 
                   (node.classList?.contains('cloud-search-filters-sidebar__filters') ||
                    node.querySelector?.('.cloud-search-filters-sidebar__filters'));
        });
    });
    
    if (hasNewFilters) {
        hideSingleValueFilters();
        if (window.globalSizeFilterManager) {
            setTimeout(() => {
                window.globalSizeFilterManager.waitForFiltersAndApply();
            }, 100);
        }
    }
});

observer.observe(document.body, {
    childList: true,
    subtree: true
});
